import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listBookings, updateBooking, deleteBooking, confirmProvisionalBooking, declineProvisionalBooking } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

import { Loader2, Search, CalendarClock, XCircle, Trash2, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { formatAppointmentLabel } from "@/lib/booking-helpers";

export const Route = createFileRoute("/_authenticated/admin/bookings")({
  component: BookingsPage,
});

const STATUSES = ["provisional", "confirmed", "arrived", "completed", "cancelled", "no_show"] as const;
const PAYMENT_STATUSES = ["unpaid", "paid", "refunded"] as const;

function BookingsPage() {
  const list = useServerFn(listBookings);
  const update = useServerFn(updateBooking);
  const remove = useServerFn(deleteBooking);
  const confirmProv = useServerFn(confirmProvisionalBooking);
  const declineProv = useServerFn(declineProvisionalBooking);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [service, setService] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [selected, setSelected] = useState<any | null>(null);
  const [cancelTarget, setCancelTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<any | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState<string>("");

  const { data: rows = [], isPending } = useQuery({
    queryKey: ["admin", "bookings", { search, status, service, from, to }],
    queryFn: () => list({
      data: {
        search,
        status: status || null,
        service: service || null,
        from: from ? new Date(from).toISOString() : null,
        to: to ? new Date(to + "T23:59:59").toISOString() : null,
      },
    }),
  });

  const provMut = useMutation({
    mutationFn: (vars: { id: string; action: "confirm" | "decline" }) =>
      vars.action === "confirm" ? confirmProv({ data: { id: vars.id } }) : declineProv({ data: { id: vars.id } }),
    onSuccess: (_r, vars) => {
      toast.success(vars.action === "confirm" ? "Booking confirmed — the patient has been emailed." : "Booking declined — the patient has been asked to choose another time.");
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mut = useMutation({
    mutationFn: (vars: { id: string; patch: any }) => update({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
      qc.invalidateQueries({ queryKey: ["admin", "patients"] });
    },
  });

  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
      qc.invalidateQueries({ queryKey: ["admin", "patients"] });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Bookings</h1>
        <p className="text-sm text-muted-foreground">Search, filter, and manage every appointment.</p>
      </div>

      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone, condition" className="pl-8" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Service</Label>
            <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Home Visit" />
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">{rows.length} bookings</CardTitle>
          <Button size="sm" variant="outline" disabled={rows.length === 0} onClick={() => exportBookingsCsv(rows)}>
            Export CSV
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No bookings match your filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Appointment</th>
                    <th className="px-4 py-3 text-left">Client</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">Location</th>
                    <th className="px-4 py-3 text-left">Payment</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b: any) => (
                    <tr key={b.id} className="border-t hover:bg-muted/30">
                      <td className="px-4 py-3">{formatAppointmentLabel(b.appointment_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{b.client_name}</div>
                        <div className="text-xs text-muted-foreground">{b.email} · {b.phone}</div>
                      </td>
                      <td className="px-4 py-3">{b.service}<div className="text-xs text-muted-foreground">{b.price}</div></td>
                      <td className="px-4 py-3">{b.location}</td>
                      <td className="px-4 py-3">
                        <div>{b.payment_method}</div>
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${b.payment_status === "paid" ? "bg-green-100 text-green-800" : b.payment_status === "failed" || b.payment_status === "cancelled" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{b.payment_status === "paid" ? "Paid by card" : b.payment_status}</span>
                        {b.payment_reference && <div className="mt-1 text-[11px] text-muted-foreground">Ref: {b.payment_reference}</div>}
                        {b.paid_at && <div className="text-[11px] text-muted-foreground">Paid {formatAppointmentLabel(b.paid_at)}</div>}
                      </td>

                      <td className="px-4 py-3">
                        {b.status === "provisional"
                          ? <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Awaiting your confirmation</span>
                          : <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs">{b.status}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {b.status === "provisional" && (
                            <>
                              <Button size="sm" disabled={provMut.isPending} onClick={() => provMut.mutate({ id: b.id, action: "confirm" })}>
                                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />Confirm
                              </Button>
                              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={provMut.isPending} onClick={() => { if (confirm(`Decline ${b.client_name}'s booking? They'll be emailed to choose another time.`)) provMut.mutate({ id: b.id, action: "decline" }); }}>
                                Decline
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={b.status === "cancelled"}
                            onClick={() => {
                              setRescheduleTarget(b);
                              setRescheduleAt(new Date(b.appointment_at).toISOString().slice(0, 16));
                            }}
                          >
                            <CalendarClock className="mr-1 h-3.5 w-3.5" />Reschedule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            disabled={b.status === "cancelled"}
                            onClick={() => setCancelTarget(b)}
                          >
                            <XCircle className="mr-1 h-3.5 w-3.5" />Cancel
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setSelected(b)}>Manage</Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(b)}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BookingDialog
        booking={selected}
        onClose={() => setSelected(null)}
        onSave={async (patch) => {
          if (!selected) return;
          await mut.mutateAsync({ id: selected.id, patch });
          setSelected(null);
        }}
        busy={mut.isPending}
      />

      <Dialog open={!!rescheduleTarget} onOpenChange={(v) => { if (!v) setRescheduleTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reschedule appointment</DialogTitle>
            <DialogDescription>
              {rescheduleTarget?.client_name} · {rescheduleTarget && formatAppointmentLabel(rescheduleTarget.appointment_at)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New date and time</Label>
            <Input type="datetime-local" value={rescheduleAt} onChange={(e) => setRescheduleAt(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleTarget(null)}>Cancel</Button>
            <Button
              disabled={mut.isPending || !rescheduleAt}
              onClick={async () => {
                if (!rescheduleTarget) return;
                await mut.mutateAsync({
                  id: rescheduleTarget.id,
                  patch: { appointment_at: new Date(rescheduleAt).toISOString() },
                });
                setRescheduleTarget(null);
              }}
            >
              {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget?.client_name} · {cancelTarget && formatAppointmentLabel(cancelTarget.appointment_at)}. This frees the slot for other clients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!cancelTarget) return;
                await mut.mutateAsync({ id: cancelTarget.id, patch: { status: "cancelled" } });
                setCancelTarget(null);
              }}
            >
              Cancel appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.client_name} · {deleteTarget && formatAppointmentLabel(deleteTarget.appointment_at)}. This removes it from the database and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deleteTarget) return;
                await delMut.mutateAsync(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function fmtDob(dob?: string | null) {
  if (!dob) return "";
  try {
    return new Date(`${dob}T00:00:00Z`).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  } catch { return dob; }
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-ZA"); } catch { return iso; }
}

function BookingDialog({ booking, onClose, onSave, busy }: { booking: any | null; onClose: () => void; onSave: (patch: any) => Promise<void>; busy: boolean }) {
  const [status, setStatus] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [appointmentAt, setAppointmentAt] = useState<string>("");
  const [privateNotes, setPrivateNotes] = useState<string>("");

  const open = !!booking;
  if (booking && !status) {
    setStatus(booking.status);
    setPaymentStatus(booking.payment_status ?? "unpaid");
    setAppointmentAt(new Date(booking.appointment_at).toISOString().slice(0, 16));
    setPrivateNotes(booking.private_notes ?? "");
  }

  async function handleSave() {
    const patch: any = {};
    if (status && status !== booking.status) patch.status = status;
    if (paymentStatus && paymentStatus !== booking.payment_status) patch.payment_status = paymentStatus;
    const iso = new Date(appointmentAt).toISOString();
    if (iso !== booking.appointment_at) patch.appointment_at = iso;
    if ((privateNotes || "") !== (booking.private_notes ?? "")) patch.private_notes = privateNotes || null;
    if (Object.keys(patch).length === 0) { onClose(); return; }
    await onSave(patch);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setStatus(""); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{booking?.client_name}</DialogTitle>
          <DialogDescription>{booking?.service} · {booking?.price}</DialogDescription>
        </DialogHeader>
        {booking && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Email</div>{booking.email}</div>
              <div><div className="text-xs text-muted-foreground">Phone</div>{booking.phone}</div>
              {booking.date_of_birth && <div><div className="text-xs text-muted-foreground">Date of birth</div>{fmtDob(booking.date_of_birth)}</div>}
              {booking.id_number && <div><div className="text-xs text-muted-foreground">{booking.id_type === "Passport" ? "Passport number" : "SA ID number"}</div>{booking.id_number}</div>}
              <div><div className="text-xs text-muted-foreground">Duration</div>{booking.duration_minutes} min</div>
              <div><div className="text-xs text-muted-foreground">Location</div>{booking.location}</div>
              <div><div className="text-xs text-muted-foreground">Payment method</div>{booking.payment_method}</div>
              {booking.address && <div className="col-span-2"><div className="text-xs text-muted-foreground">Address</div>{booking.address}</div>}
              <div className="col-span-2"><div className="text-xs text-muted-foreground">Condition</div>{booking.condition}</div>
              {booking.medical_aid && <div><div className="text-xs text-muted-foreground">Medical aid</div>{booking.medical_aid}</div>}
              {booking.medical_aid_number && <div><div className="text-xs text-muted-foreground">Membership #</div>{booking.medical_aid_number}</div>}
              {booking.notes && <div className="col-span-2"><div className="text-xs text-muted-foreground">Client notes</div>{booking.notes}</div>}
              <div><div className="text-xs text-muted-foreground">Booked at</div>{fmtDateTime(booking.created_at)}</div>
              <div><div className="text-xs text-muted-foreground">Booking ID</div><span className="font-mono text-xs">{booking.id}</span></div>
              {booking.arrived_at && <div><div className="text-xs text-muted-foreground">Arrived</div>{fmtDateTime(booking.arrived_at)}</div>}
              {booking.completed_at && <div><div className="text-xs text-muted-foreground">Completed</div>{fmtDateTime(booking.completed_at)}</div>}
              {booking.cancelled_at && <div><div className="text-xs text-muted-foreground">Cancelled</div>{fmtDateTime(booking.cancelled_at)}</div>}
              {booking.google_calendar_event_id && <div className="col-span-2"><div className="text-xs text-muted-foreground">Google Calendar event</div><span className="font-mono text-xs">{booking.google_calendar_event_id}</span></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Reschedule</Label>
                <Input type="datetime-local" value={appointmentAt} onChange={(e) => setAppointmentAt(e.target.value)} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Private notes (admin only)</Label>
              <Textarea rows={4} value={privateNotes} onChange={(e) => setPrivateNotes(e.target.value)} placeholder="Treatment notes, follow up plan, etc." />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleSave} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Downloads the filtered bookings as a CSV for import into a practice diary or Excel. */
function exportBookingsCsv(rows: any[]) {
  const headers = [
    "Appointment",
    "Client",
    "Email",
    "Phone",
    "Service",
    "Duration (min)",
    "Price",
    "Location",
    "Address",
    "Payment method",
    "Payment status",
    "Status",
    "Condition",
  ];
  const cell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  for (const b of rows) {
    lines.push(
      [
        new Date(b.appointment_at).toISOString(),
        b.client_name,
        b.email,
        b.phone,
        b.service,
        b.duration_minutes,
        b.price,
        b.location,
        b.address,
        b.payment_method,
        b.payment_status,
        b.status,
        b.condition,
      ]
        .map(cell)
        .join(","),
    );
  }
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ns-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
